import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/LanguageSwitcher';

export default function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-xl flex items-center justify-center shadow-lg">
              <i className="ri-luggage-cart-line text-2xl text-white"></i>
            </div>
            <div className="flex flex-col">
              <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                AIR BAGGAGE REPAIR
              </span>
              <span className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-white/80'}`}>
                Premium B2B Solutions
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <button
              onClick={() => scrollToSection('services')}
              className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#4ECDC4]'
              }`}
            >
              {t('nav.solutions')}
            </button>
            <button
              onClick={() => scrollToSection('ecosystem')}
              className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#4ECDC4]'
              }`}
            >
              {t('nav.technology')}
            </button>
            <button
              onClick={() => scrollToSection('guarantee')}
              className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#4ECDC4]'
              }`}
            >
              {t('nav.advantages')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-[#FF6B6B]' : 'text-white hover:text-[#4ECDC4]'
              }`}
            >
              {t('nav.contact')}
            </button>

            <LanguageSwitcher />

            <a
              href="/contact"
              className="px-6 py-2.5 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300 whitespace-nowrap"
            >
              {t('nav.getInfo')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-10 h-10 flex items-center justify-center ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-xl">
          <div className="px-4 py-6 space-y-4">
            <button
              onClick={() => scrollToSection('services')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.solutions')}
            </button>
            <button
              onClick={() => scrollToSection('ecosystem')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.technology')}
            </button>
            <button
              onClick={() => scrollToSection('guarantee')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.advantages')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.contact')}
            </button>
            <a
              href="/contact"
              className="block w-full text-center px-6 py-3 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300"
            >
              {t('nav.getInfo')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}